export type StrategyLevel = "BEGINNER" | "INTERMEDIATE" | "ADVANCED";

export type StrategyColor = "blue" | "cyan" | "purple" | "orange";

export type StrategyCatalogEntry = {
  slug: string;
  name: string;
  shortDescription: string;
  fullDescription: string;
  tag: StrategyLevel;
  color: StrategyColor;
  matchCount: number;
  timeframe: string;
  focus: string;
  seoTitle: string;
  seoDescription: string;
  keywords: string[];
};

export const strategyCatalog: StrategyCatalogEntry[] = [
  {
    slug: "hiran",
    name: "Weekly Trend Triad",
    shortDescription: "Weekly HMA + RSI + MACD alignment",
    fullDescription:
      "Advanced weekly PSX strategy combining Hull Moving Average, RSI, and MACD to surface strong trend-aligned setups when all indicators agree.",
    tag: "ADVANCED",
    color: "orange",
    matchCount: 24,
    timeframe: "Weekly",
    focus: "Trend + momentum confluence",
    seoTitle: "Weekly Trend Triad Strategy | PSX HMA, RSI, and MACD Confluence",
    seoDescription:
      "Learn how the Weekly Trend Triad strategy scans Pakistan Stock Exchange stocks using Hull Moving Average, RSI, and MACD alignment for higher-quality weekly trend setups.",
    keywords: [
      "PSX weekly strategy",
      "Pakistan Stock Exchange trend strategy",
      "HMA RSI MACD strategy",
      "PSX weekly scanner",
      "weekly trend triad",
    ],
  },
  {
    slug: "hiran-confluence",
    name: "Retest Breakout Edge",
    shortDescription: "EMA + S/R confluence with clean retest",
    fullDescription:
      "PSX breakout strategy that looks for EMA and support or resistance confluence, then a clean retest to confirm momentum before entry.",
    tag: "ADVANCED",
    color: "orange",
    matchCount: 18,
    timeframe: "Daily",
    focus: "Retest confirmation",
    seoTitle: "Retest Breakout Edge Strategy | PSX EMA and Support Resistance Retests",
    seoDescription:
      "Review the Retest Breakout Edge strategy for PSX stocks using EMA alignment, breakout confirmation, and retest quality to improve entry selection.",
    keywords: [
      "PSX breakout strategy",
      "Pakistan Stock Exchange retest breakout",
      "EMA support resistance strategy",
      "PSX breakout scanner",
      "retest breakout edge",
    ],
  },
  {
    slug: "next-day-potential",
    name: "Next-Day Momentum Edge",
    shortDescription: "Next-session bullish momentum candidates",
    fullDescription:
      "PSX scanner that scores how many bullish conditions align across price action, momentum, trend strength and volume, and ranks next-session candidates by that score.",
    tag: "ADVANCED",
    color: "orange",
    matchCount: 18,
    timeframe: "Daily",
    focus: "Next-session momentum",
    seoTitle: "Next-Day Momentum Edge | PSX Next Session Momentum Scanner",
    seoDescription:
      "Track PSX stocks with next-session momentum potential using a setup score built from price structure, momentum, ATR stability, and volume participation.",
    keywords: [
      "PSX momentum scanner",
      "Pakistan Stock Exchange next day stocks",
      "next session momentum PSX",
      "PSX momentum setup scanner",
      "tomorrow potential stocks",
    ],
  },
  {
    slug: "renko-ichimoku",
    name: "Renko + Ichimoku",
    shortDescription: "Renko turn + Ichimoku cloud confluence",
    fullDescription:
      "PSX strategy that combines Renko brick turns with Ichimoku Cloud alignment to surface higher-conviction trend continuation and reversal setups.",
    tag: "ADVANCED",
    color: "orange",
    matchCount: 18,
    timeframe: "Daily",
    focus: "Trend + cloud confirmation",
    seoTitle: "Renko Ichimoku Strategy | PSX Renko and Ichimoku Cloud Signals",
    seoDescription:
      "Explore the PSX Renko Ichimoku strategy with Renko trend shifts, Ichimoku cloud alignment, live scan results, and backtest context for trend-following setups.",
    keywords: [
      "PSX renko ichimoku",
      "Pakistan Stock Exchange Ichimoku strategy",
      "Renko cloud strategy",
      "PSX trend following scanner",
      "renko ichimoku backtest",
    ],
  },
  {
    slug: "fib-golden-zone",
    name: "Fib Golden Zone Pullback",
    shortDescription: "Price retraces into 61.8% to 65% zone",
    fullDescription:
      "PSX pullback scanner for stocks retracing from a recent top into the Fibonacci golden zone, highlighting continuation setups with disciplined risk.",
    tag: "ADVANCED",
    color: "orange",
    matchCount: 0,
    timeframe: "Daily",
    focus: "Trend pullback entries",
    seoTitle: "Fib Golden Zone Pullback Strategy | PSX Fibonacci Pullback Scanner",
    seoDescription:
      "Use the Fib Golden Zone Pullback strategy to find PSX stocks retracing into a key Fibonacci demand zone after an upswing, with scan output and backtest context.",
    keywords: [
      "PSX fibonacci strategy",
      "Pakistan Stock Exchange pullback scanner",
      "fib golden zone PSX",
      "PSX continuation pullback",
      "fibonacci retracement stocks Pakistan",
    ],
  },
  {
    slug: "moving-average-crossover",
    name: "Moving Average Crossover Strategy",
    shortDescription: "Identify trends using MA crossovers",
    fullDescription:
      "Classic moving-average crossover strategy for PSX stocks using faster and slower averages to identify trend changes and basic entry timing.",
    tag: "BEGINNER",
    color: "blue",
    matchCount: 12,
    timeframe: "Daily",
    focus: "Trend entry basics",
    seoTitle: "Moving Average Crossover Strategy | PSX Beginner Trend Signals",
    seoDescription:
      "Understand how moving average crossovers work on PSX stocks, including golden cross and death cross behavior for beginner-friendly trend trading.",
    keywords: [
      "PSX moving average crossover",
      "Pakistan Stock Exchange beginner strategy",
      "golden cross PSX",
      "death cross PSX",
      "PSX trend beginner",
    ],
  },
  {
    slug: "momentum-rsi",
    name: "Momentum RSI Step Up",
    shortDescription: "Capture momentum shifts with RSI",
    fullDescription:
      "Beginner-friendly RSI momentum strategy for PSX stocks that highlights overbought, oversold, and recovery conditions around key momentum zones.",
    tag: "BEGINNER",
    color: "cyan",
    matchCount: 8,
    timeframe: "Daily",
    focus: "Momentum reversal basics",
    seoTitle: "Momentum RSI Strategy | PSX RSI Overbought and Oversold Signals",
    seoDescription:
      "Learn how to use RSI on PSX stocks to identify momentum shifts, overbought conditions, oversold rebounds, and reversal opportunities.",
    keywords: [
      "PSX RSI strategy",
      "Pakistan Stock Exchange RSI scanner",
      "RSI reversal PSX",
      "PSX momentum strategy",
      "overbought oversold Pakistan stocks",
    ],
  },
  {
    slug: "golden-cross",
    name: "Golden & Death Cross Strategy",
    shortDescription: "Track bullish and bearish MA crosses, newest first",
    fullDescription:
      "Track confirmed golden crosses and death crosses on PSX stocks, with the event date and price change since each 50-day / 200-day moving average cross.",
    tag: "INTERMEDIATE",
    color: "purple",
    matchCount: 5,
    timeframe: "Daily",
    focus: "Long-term trend capture",
    seoTitle: "Golden Cross Strategy | PSX Long-Term Trend Signals",
    seoDescription:
      "Review the Golden Cross strategy for PSX stocks using the 50-day and 200-day moving averages to identify major long-term bullish regime shifts.",
    keywords: [
      "PSX golden cross",
      "Pakistan Stock Exchange long term strategy",
      "50 day 200 day crossover Pakistan stocks",
      "PSX bullish crossover",
      "golden cross strategy Pakistan",
    ],
  },
  {
    slug: "rsi-reversal",
    name: "RSI Reversal",
    shortDescription: "Find reversal points using RSI",
    fullDescription:
      "Intermediate reversal strategy for PSX stocks using RSI extremes and normalization behavior to locate potential turning points.",
    tag: "INTERMEDIATE",
    color: "purple",
    matchCount: 15,
    timeframe: "Daily",
    focus: "Mean reversion",
    seoTitle: "RSI Reversal Strategy | PSX Mean Reversion and Turning Points",
    seoDescription:
      "Study the RSI Reversal strategy for PSX stocks to identify oversold and overbought extremes, divergence context, and possible turning points.",
    keywords: [
      "PSX RSI reversal",
      "Pakistan Stock Exchange mean reversion",
      "RSI divergence strategy Pakistan",
      "PSX reversal scanner",
      "RSI oversold stocks PSX",
    ],
  },
  {
    slug: "high-volume-breaks",
    name: "High Volume Breaks",
    shortDescription: "Volume-based breakout strategy",
    fullDescription:
      "Intermediate breakout strategy for PSX stocks that combines consolidation breaks with stronger-than-normal volume to confirm participation.",
    tag: "INTERMEDIATE",
    color: "purple",
    matchCount: 10,
    timeframe: "Daily",
    focus: "Breakout confirmation",
    seoTitle: "High Volume Breakout Strategy | PSX Breakouts With Volume Confirmation",
    seoDescription:
      "Find PSX stocks breaking out of consolidation with stronger volume participation using this breakout strategy focused on turnover confirmation.",
    keywords: [
      "PSX volume breakout",
      "Pakistan Stock Exchange breakout strategy",
      "high volume breakout PSX",
      "PSX momentum breakout scanner",
      "volume confirmation stocks Pakistan",
    ],
  },
  {
    slug: "multi-timeframe-confluence",
    name: "Daily + Weekly Confluence",
    shortDescription: "Multi-timeframe confirmation signals",
    fullDescription:
      "Advanced PSX strategy that looks for daily entries aligned with the weekly trend to improve trade quality through cross-timeframe confluence.",
    tag: "ADVANCED",
    color: "orange",
    matchCount: 3,
    timeframe: "Daily + Weekly",
    focus: "Cross-timeframe alignment",
    seoTitle: "Daily Weekly Confluence Strategy | PSX Multi-Timeframe Signals",
    seoDescription:
      "Use daily and weekly chart alignment to find higher-quality PSX setups with this multi-timeframe confluence strategy for serious traders.",
    keywords: [
      "PSX multi timeframe strategy",
      "Pakistan Stock Exchange weekly daily confluence",
      "PSX advanced strategy",
      "daily weekly chart alignment",
      "multi timeframe scanner Pakistan",
    ],
  },
  {
    slug: "ema-pullback",
    name: "EMA Pullback Buy",
    shortDescription: "Buy the dip at key moving-average support",
    fullDescription:
      "Beginner-friendly continuation strategy for PSX stocks that waits for price to pull back within striking distance of a key EMA or SMA before looking for a bounce entry in the direction of the trend.",
    tag: "BEGINNER",
    color: "blue",
    matchCount: 0,
    timeframe: "Daily",
    focus: "Pullback to MA support",
    seoTitle: "EMA Pullback Strategy | Buy the Dip at Key Moving Averages on PSX",
    seoDescription:
      "Learn the EMA pullback strategy for PSX stocks: wait for trending stocks to retrace near a key EMA or SMA, then enter on the bounce with defined risk.",
    keywords: [
      "PSX EMA pullback",
      "buy the dip Pakistan stocks",
      "moving average support PSX",
      "PSX pullback strategy",
      "EMA bounce strategy",
    ],
  },
  {
    slug: "macd-turn",
    name: "MACD Turn",
    shortDescription: "Histogram flips from negative to positive",
    fullDescription:
      "Beginner momentum strategy for PSX stocks built on the MACD histogram turning positive after a bearish stretch — the earliest MACD confirmation that momentum is shifting bullish.",
    tag: "BEGINNER",
    color: "cyan",
    matchCount: 0,
    timeframe: "Daily",
    focus: "Momentum flip basics",
    seoTitle: "MACD Turn Strategy | PSX MACD Histogram Flip Signals",
    seoDescription:
      "Use the MACD Turn strategy to catch PSX stocks the moment the MACD histogram flips from negative to positive after a bearish stretch.",
    keywords: [
      "PSX MACD strategy",
      "MACD histogram flip",
      "Pakistan Stock Exchange MACD scanner",
      "PSX momentum turn",
      "MACD bullish crossover PSX",
    ],
  },
  {
    slug: "trendline-support-bounce",
    name: "Trendline Support Bounce",
    shortDescription: "Entries at tested rising support lines",
    fullDescription:
      "Intermediate pullback strategy for PSX stocks sitting at or near auto-detected pivot-low support trendlines, favouring lines with more touches and no recent violations.",
    tag: "INTERMEDIATE",
    color: "purple",
    matchCount: 0,
    timeframe: "Daily",
    focus: "Support-line entries",
    seoTitle: "Trendline Support Bounce Strategy | PSX Auto-Detected Support Lines",
    seoDescription:
      "Trade bounces off tested support trendlines on PSX stocks using auto-detected pivot-low lines ranked by touches, violations, and proximity.",
    keywords: [
      "PSX trendline strategy",
      "support trendline bounce",
      "Pakistan Stock Exchange support scanner",
      "PSX trendline support",
      "trendline trading Pakistan stocks",
    ],
  },
  {
    slug: "slingshot",
    name: "Slingshot Momentum",
    shortDescription: "Fresh cross above the EMA of highs",
    fullDescription:
      "Intermediate momentum-ignition strategy for PSX stocks where the close crosses above the 4-EMA of highs for the first time in several bars — a fresh momentum entry before the crowd.",
    tag: "INTERMEDIATE",
    color: "purple",
    matchCount: 0,
    timeframe: "Daily",
    focus: "Momentum ignition",
    seoTitle: "Slingshot Momentum Strategy | PSX EMA-of-Highs Breakout Signals",
    seoDescription:
      "Catch fresh PSX momentum with the Slingshot strategy: the first close above the 4-EMA of highs in several bars signals early trend ignition.",
    keywords: [
      "PSX slingshot strategy",
      "EMA of highs cross",
      "Pakistan Stock Exchange momentum entry",
      "PSX fresh momentum signal",
      "early breakout strategy PSX",
    ],
  },
  {
    slug: "rsi-divergence-reversal",
    name: "RSI Divergence Reversal",
    shortDescription: "Price makes new lows, RSI doesn't",
    fullDescription:
      "Intermediate reversal strategy for PSX stocks built on bullish RSI divergence — price prints a lower low while RSI holds a higher low, an early warning that selling pressure is fading.",
    tag: "INTERMEDIATE",
    color: "purple",
    matchCount: 0,
    timeframe: "Daily",
    focus: "Divergence-led reversals",
    seoTitle: "RSI Divergence Reversal Strategy | PSX Bullish Divergence Signals",
    seoDescription:
      "Find early PSX reversal candidates with bullish RSI divergence: price making lower lows while momentum quietly improves underneath.",
    keywords: [
      "PSX RSI divergence",
      "bullish divergence Pakistan stocks",
      "RSI divergence reversal strategy",
      "PSX divergence scanner",
      "momentum divergence PSX",
    ],
  },
  {
    slug: "minervini-vcp",
    name: "Minervini VCP Breakout",
    shortDescription: "Trend Template + volatility contraction",
    fullDescription:
      "Advanced PSX strategy based on Mark Minervini's SEPA method: a strict Trend Template filter finds stage-2 uptrends, then Volatility Contraction Pattern (VCP) detection surfaces tightening bases with a defined pivot buy point.",
    tag: "ADVANCED",
    color: "orange",
    matchCount: 0,
    timeframe: "Daily + Weekly",
    focus: "VCP base breakouts",
    seoTitle: "Minervini VCP Strategy | PSX Trend Template and Volatility Contraction",
    seoDescription:
      "Apply Mark Minervini's Trend Template and VCP detection to PSX stocks: stage-2 uptrends, tightening contractions, RS ranking, and pivot buy points.",
    keywords: [
      "Minervini PSX",
      "VCP strategy Pakistan",
      "trend template PSX",
      "volatility contraction pattern",
      "PSX RS rating scanner",
    ],
  },
];

export const strategyCatalogBySlug = Object.fromEntries(
  strategyCatalog.map((strategy) => [strategy.slug, strategy]),
) as Record<string, StrategyCatalogEntry>;

export const strategySlugs = strategyCatalog.map((strategy) => strategy.slug);
